import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { CheckCircle2, MapPin, Loader2 } from "lucide-react";
import AppBadges from "./AppBadges";
import { toast } from "sonner";
import { joinWaitlist } from "../lib/api";

export default function WaitlistModal({ open, onOpenChange }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [city, setCity] = useState("Lagos");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Please enter your full name.");
      return;
    }
    const validEmail = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);
    if (!validEmail) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (phone.replace(/\D/g, "").length < 10) {
      toast.error("Please enter a valid phone number.");
      return;
    }
    setLoading(true);
    try {
      await joinWaitlist({ name, email, phone, whatsapp, city });
      // keep a local copy too
      const list = JSON.parse(localStorage.getItem("id_waitlist") || "[]");
      list.push({ name, email, phone, whatsapp, city, at: new Date().toISOString() });
      localStorage.setItem("id_waitlist", JSON.stringify(list));
      setDone(true);
      toast.success("You're on the list!", { description: "Check your email — we've sent a welcome message." });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const reset = (v) => {
    onOpenChange(v);
    if (!v) setTimeout(() => { setDone(false); setName(""); setEmail(""); setPhone(""); setWhatsapp(""); }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={reset}>
      <DialogContent className="bg-[#0e1311] border-[#2fd645]/25 text-white sm:max-w-md">
        {!done ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-display font-extrabold text-2xl">Join the waitlist</DialogTitle>
              <DialogDescription className="text-white/60">
                Be first to ride when InstantDrive launches in your city.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="mt-2 space-y-4">
              <div className="space-y-2">
                <Label className="text-white/80">Full name</Label>
                <Input
                  type="text"
                  placeholder="Ada Obi"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="h-12 bg-[#0a0e0c] border-white/15 text-white placeholder:text-white/40 focus-visible:ring-[#2fd645]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white/80">Email address</Label>
                <Input
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 bg-[#0a0e0c] border-white/15 text-white placeholder:text-white/40 focus-visible:ring-[#2fd645]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white/80">Phone number</Label>
                <Input
                  type="tel"
                  placeholder="080 0000 0000"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="h-12 bg-[#0a0e0c] border-white/15 text-white placeholder:text-white/40 focus-visible:ring-[#2fd645]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white/80">WhatsApp number <span className="text-white/40 font-normal">(optional)</span></Label>
                <Input
                  type="tel"
                  placeholder="WhatsApp number for updates"
                  value={whatsapp}
                  onChange={(e) => setWhatsapp(e.target.value)}
                  className="h-12 bg-[#0a0e0c] border-white/15 text-white placeholder:text-white/40 focus-visible:ring-[#2fd645]"
                />
                <p className="text-xs text-white/40">We'll send launch updates here on WhatsApp too.</p>
              </div>
              <div className="space-y-2">
                <Label className="text-white/80">City</Label>
                <div className="grid grid-cols-2 gap-3">
                  {["Lagos", "Abuja"].map((c) => (
                    <button
                      type="button"
                      key={c}
                      onClick={() => setCity(c)}
                      className={`flex items-center justify-center gap-2 h-12 rounded-xl border font-semibold transition-all ${
                        city === c
                          ? "border-[#2fd645] bg-[#2fd645]/10 text-[#2fd645]"
                          : "border-white/15 text-white/70 hover:border-white/30"
                      }`}
                    >
                      <MapPin className="h-4 w-4" /> {c}
                    </button>
                  ))}
                </div>
              </div>
              <Button type="submit" disabled={loading} className="w-full h-12 bg-[#2fd645] text-[#06210b] font-bold hover:bg-[#46ff5c] rounded-xl disabled:opacity-70">
                {loading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Joining…</>) : "Join waitlist"}
              </Button>
            </form>
          </>
        ) : (
          <div className="text-center py-6">
            <div className="mx-auto h-16 w-16 rounded-full bg-[#2fd645]/15 border border-[#2fd645]/40 flex items-center justify-center">
              <CheckCircle2 className="h-9 w-9 text-[#2fd645]" />
            </div>
            <h3 className="mt-5 font-display font-extrabold text-2xl">You&apos;re in!</h3>
            <p className="mt-2 text-white/60">
              Thanks for joining. We&apos;ll email you the moment InstantDrive goes live in {city}.
            </p>
            <p className="mt-6 text-sm text-white/50">Get a head start — download the app:</p>
            <div className="mt-3 flex justify-center"><AppBadges /></div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
